import { uploadImageToCos, compressImage } from '@/utils/cosUpload'

/**
 * 评论配图的选择 / 预览 / 上传逻辑。
 *
 * 主评论框（components/comment/index.vue）和每条评论下的回复框
 * （components/comment/Reply.vue）都要这套东西，而两个组件的输入区是各写一份的
 * （不是同一个子组件），所以抽成 mixin 而不是复制两遍 —— 复制的代价是以后改上限、
 * 改压缩参数、改错误提示都得记得改两处。
 *
 * 流程沿用发帖页（view/add/index.vue）和举报弹窗（components/report-dialog）
 * 已经在用的那一套「选的时候不传，发布时才传」：
 *   1. el-upload 用 :http-request 接管默认上传，只做压缩 + 生成 blob 预览地址；
 *   2. previewUrl -> File 存在 commentPendingFiles 里；
 *   3. 点发布时 uploadCommentImagesToCos() 逐个传 COS，换成正式地址再提交评论。
 *
 * 为什么不选完就传：用户很可能选了图又改主意关掉回复框，选一张传一张会在
 * 对象存储里留一堆没有任何记录引用的文件，既占空间又没法清理。
 */

/**
 * 一条评论最多几张图。
 * 必须和后端 CommentImageUtils.MAX_COMMENT_IMAGE_COUNT 一致 ——
 * 前端这道只是给用户即时反馈，越过它的请求后端会直接拒掉。
 */
export const COMMENT_IMAGE_MAX_COUNT = 3

/**
 * 单张图片大小上限（MB），与举报材料、发帖配图保持同一档。
 * 服务端上传接口本身也有 10MB 限制，这里收得更紧一点：
 * 评论里的图只会以缩略图展示，没必要让用户上传一张 8MB 的原图。
 */
export const COMMENT_IMAGE_MAX_SIZE = 5

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

export default {
  data () {
    return {
      // 模板里读不到模块级常量，透一份到 data（不是 computed —— 它是个常量，
      // 走 computed 只会多一层无谓的依赖追踪）
      commentImageMaxCount: COMMENT_IMAGE_MAX_COUNT,
      // 预览地址列表，元素是 URL.createObjectURL 出来的 blob 地址
      commentImageList: [],
      // previewUrl -> File 映射，发布时按这个找原始文件去上传
      commentPendingFiles: {}
    }
  },

  computed: {
    hasCommentImages () {
      return this.commentImageList.length > 0
    }
  },

  beforeDestroy () {
    // blob 地址不撤销的话，那份内存要等页面关闭才释放。
    // 评论区的回复框是每条评论一个实例、随列表翻页大量销毁，攒起来不是小数目
    this.revokeCommentImagePreviews()
  },

  methods: {
    /**
     * el-upload 的 before-upload：类型和大小校验。
     * 返回 false 会阻止后续的 http-request。
     */
    beforeCommentImageUpload (file) {
      if (this.commentImageList.length >= COMMENT_IMAGE_MAX_COUNT) {
        this.$toast.error(`最多上传${COMMENT_IMAGE_MAX_COUNT}张图片`)
        return false
      }
      if (!VALID_IMAGE_TYPES.includes(String(file.type || '').toLowerCase())) {
        this.$toast.error('只支持 JPG、PNG、GIF、WebP 格式的图片')
        return false
      }
      if (file.size / 1024 / 1024 > COMMENT_IMAGE_MAX_SIZE) {
        this.$toast.error(`图片大小不能超过${COMMENT_IMAGE_MAX_SIZE}MB`)
        return false
      }
      return true
    },

    /**
     * el-upload 的 http-request：接管默认上传行为。
     *
     * 这里只压缩 + 生成预览地址，真正的上传推到点「发布」时（见
     * uploadCommentImagesToCos）。压缩放在选图这一刻做而不是发布时做：
     * 用户选完图到点发布之间有几秒到几十秒，把 CPU 开销挪到这段空闲时间里，
     * 点发布之后就只剩网络时间，感觉更快。
     */
    async handleCommentImageUpload (param) {
      try {
        const processedFile = await compressImage(param.file)
        const previewUrl = URL.createObjectURL(processedFile)
        this.commentImageList.push(previewUrl)
        // $set：commentPendingFiles 是个普通对象，新增 key 不会自动变成响应式
        this.$set(this.commentPendingFiles, previewUrl, processedFile)
        param.onSuccess({ data: previewUrl })
      } catch (error) {
        console.error('评论图片处理失败:', error)
        param.onError(error)
        this.$toast.error(error.message || '图片处理失败，请重试')
      }
    },

    /**
     * 移除一张已选的图片。
     * 必须同时撤销 blob 地址并从 pendingFiles 里删掉，否则那张图的内存
     * 和 File 引用会一直挂着，而它已经不在界面上了。
     */
    removeCommentImage (index) {
      const previewUrl = this.commentImageList[index]
      if (previewUrl && this.commentPendingFiles[previewUrl]) {
        URL.revokeObjectURL(previewUrl)
        this.$delete(this.commentPendingFiles, previewUrl)
      }
      this.commentImageList.splice(index, 1)
    },

    /**
     * 把已选的图片全部传到 COS，返回正式地址数组。
     *
     * compress: false —— 选图时已经压过一遍了（见 handleCommentImageUpload），
     * 再压一次纯属浪费 CPU，还会二次损失画质。
     *
     * 逐个串行传而不是 Promise.all：一条评论最多 3 张，并发省下的时间有限，
     * 而串行能让「传到第几张失败」这件事可定位（并发失败只知道整批挂了）。
     *
     * @returns {Promise<string[]>} 图片地址；没有图时是空数组。
     *          任意一张失败会抛出，调用方负责提示并中止发布 ——
     *          少传一张就把评论发出去，用户看不出图丢了。
     */
    async uploadCommentImagesToCos () {
      if (!this.commentImageList.length) return []

      const uploaded = []
      for (const previewUrl of this.commentImageList) {
        const file = this.commentPendingFiles[previewUrl]
        if (file) {
          // eslint-disable-next-line no-await-in-loop
          const cosUrl = await uploadImageToCos(file, {
            directory: 'comment',
            maxSize: COMMENT_IMAGE_MAX_SIZE,
            compress: false
          })
          uploaded.push(cosUrl)
        } else {
          // 已经是远程地址（当前没有这种入口，留着是为了以后做「编辑评论」时不用改这里）
          uploaded.push(previewUrl)
        }
      }
      return uploaded
    },

    /**
     * 清空已选图片（发布成功、或取消回复时调用）。
     */
    resetCommentImages () {
      this.revokeCommentImagePreviews()
      this.commentImageList = []
      this.commentPendingFiles = {}
    },

    /**
     * 撤销所有 blob 预览地址，但不动列表本身。
     * 单独抽出来是因为 beforeDestroy 里只需要释放内存，没必要再走一遍响应式赋值。
     */
    revokeCommentImagePreviews () {
      Object.keys(this.commentPendingFiles || {}).forEach(previewUrl => {
        URL.revokeObjectURL(previewUrl)
      })
    }
  }
}
